import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Save, Plus, Trash2, Sparkles, Lightbulb, Pencil, Download, Upload } from "lucide-react";
import AutocompleteInput from "@/components/AutocompleteInput";

const CATS = [
  { id: "", name: "All disciplines" },
  { id: "barrel_racing", name: "Barrel Racing" },
  { id: "team_roping", name: "Team Roping" },
  { id: "tie_down_roping", name: "Tie-Down Roping" },
  { id: "steer_wrestling", name: "Steer Wrestling" },
  { id: "breakaway", name: "Breakaway" },
];

const CAT_NAME = Object.fromEntries(CATS.map((c) => [c.id, c.name]));

const DEFAULT_TIPS = {
  barrel_racing: "Run starts when horse breaks the timing eye / starts toward first barrel. Ends 1-2s after the third barrel rounded — typical clip 12-22s.",
  team_roping: "Header swings rope, releases. Heeler catches feet. Run is over when both ropes are taut and horses face. Typical clip 8-15s — never over 25s.",
  tie_down_roping: "Calf released, roper chases, ropes, dismounts, ties. Run ends with hands up signal. 10-18s typical.",
  steer_wrestling: "Bulldogger leaves box, drops onto steer, throws it. Stop right after steer is on the ground. 4-12s — short.",
  breakaway: "Calf released, roper throws once. Run ends when string breaks from saddle. Very short — 3-8s.",
};

const TEMPLATES = [
  "Always cut at the start of the action, not when the rider enters the arena.",
  "Skip practice runs and warm-ups — only competition runs.",
  "If the on-screen scoreboard shows the time, the cut should end right after that flash.",
  "Don't cut runs where the horse refuses or fails to leave the box.",
  "If multiple riders are visible, return one cut per rider.",
];

export default function Settings() {
  const [s, setS] = useState({ brand_text: "", brand_logo_url: "", title_card_template: "", apply_to_clips: false });
  const [saving, setSaving] = useState(false);
  const [tips, setTips] = useState([]);
  const [newTip, setNewTip] = useState("");
  const [newCat, setNewCat] = useState("");
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data));
    refreshTips();
  }, []);

  const refreshTips = () => {
    api.get("/coaching-tips").then((r) => setTips(r.data.tips || []));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", s);
      toast.success("Settings saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const addTip = async (text, category = "") => {
    if (!text.trim()) return;
    setAdding(true);
    try {
      await api.post("/coaching-tips", { text, category });
      setNewTip("");
      refreshTips();
      toast.success("Tip added — AI will use this on next analysis");
    } catch (err) {
      toast.error("Failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setAdding(false);
    }
  };

  const delTip = async (id) => {
    await api.delete(`/coaching-tips/${id}`);
    refreshTips();
  };

  return (
    <div className="px-6 md:px-12 py-10 max-w-[1000px]" data-testid="settings-page">
      <div className="border-l-4 border-orange-600 pl-5 mb-8">
        <div className="label-tiny mb-2">Settings</div>
        <h1 className="font-display uppercase text-5xl font-bold leading-[0.95]">Settings</h1>
        <p className="text-stone-400 mt-3">Brand watermark for reels. Coaching tips for the AI.</p>
      </div>

      {/* Branding */}
      <section className="mb-10">
        <h2 className="font-display uppercase text-2xl font-bold mb-3 flex items-center gap-2">Branding</h2>
        <div className="space-y-5 border border-stone-800 p-6">
          <div>
            <div className="label-tiny mb-1">Brand text (watermark on reels)</div>
            <AutocompleteInput field="brand_text" value={s.brand_text || ""} onChange={(v) => setS({ ...s, brand_text: v })} placeholder="e.g. Royal Crown Rodeo" className="field-input" data-testid="brand-text-input" />
            <div className="text-[10px] text-stone-500 mt-1">Appears in lower-right corner of every reel.</div>
          </div>
          <div>
            <div className="label-tiny mb-1">Brand logo URL (optional)</div>
            <AutocompleteInput field="url" value={s.brand_logo_url || ""} onChange={(v) => setS({ ...s, brand_logo_url: v })} placeholder="https://…" className="field-input" data-testid="brand-logo-input" />
          </div>
          <div>
            <div className="label-tiny mb-1">Title card template</div>
            <AutocompleteInput field="title_template" value={s.title_card_template || ""} onChange={(v) => setS({ ...s, title_card_template: v })} placeholder="{rider} · {category}" className="field-input" data-testid="title-template-input" />
            <div className="text-[10px] text-stone-500 mt-1">Variables: {"{rider}"}, {"{category}"}, {"{sub_label}"}, {"{date}"}.</div>
          </div>
          <label className="flex items-center gap-2 text-sm text-stone-300 pt-3 border-t border-stone-800">
            <input type="checkbox" checked={!!s.apply_to_clips} onChange={(e) => setS({ ...s, apply_to_clips: e.target.checked })} data-testid="apply-clips-toggle" />
            Apply brand text to individual clips when generating
          </label>
          <label className="flex items-center gap-2 text-sm text-stone-300 pt-3 border-t border-stone-800">
            <input type="checkbox" checked={!!s.auto_compress} onChange={(e) => setS({ ...s, auto_compress: e.target.checked })} data-testid="auto-compress-toggle" />
            Auto-compress uploaded videos to 720p (~5-7× smaller)
          </label>
          {s.auto_compress && (
            <div className="pl-6">
              <div className="label-tiny mb-1">Compress only files larger than (MB)</div>
              <input
                type="number"
                value={s.auto_compress_threshold_mb || 500}
                onChange={(e) => setS({ ...s, auto_compress_threshold_mb: parseInt(e.target.value, 10) || 500 })}
                className="field-input !w-32"
                data-testid="auto-compress-threshold"
              />
              <div className="text-[10px] text-stone-500 mt-1">Default 500 MB. Smaller files skip compression.</div>
            </div>
          )}
          <label className="flex items-start gap-2 text-sm text-stone-300 pt-3 border-t border-stone-800">
            <input
              type="checkbox"
              checked={s.auto_identify_riders !== false}
              onChange={(e) => setS({ ...s, auto_identify_riders: e.target.checked })}
              data-testid="auto-identify-riders-toggle"
              className="mt-1"
            />
            <span>
              Auto-identify riders from announcer audio after every AI Suggest / Deep Scan
              <span className="block text-[10px] text-stone-500 mt-0.5">
                Uses Whisper to transcribe announcer commentary, then Gemini to extract names like "Sarah Johnson on Dusty". Only fills cuts that have no rider name yet — never overwrites manual entries. Adds ~30-90s per video.
              </span>
            </span>
          </label>

          {/* Client storefront pricing */}
          <div className="pt-3 border-t border-stone-800 space-y-2">
            <div className="label-tiny mb-1">Client storefront pricing</div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-[10px] text-stone-500 mb-1">Default price per clip</div>
                <input
                  type="number"
                  step="0.01"
                  min="0.50"
                  value={s.clip_price_default ?? 10}
                  onChange={(e) => setS({ ...s, clip_price_default: parseFloat(e.target.value) || 0 })}
                  className="field-input"
                  data-testid="clip-price-input"
                />
              </div>
              <div>
                <div className="text-[10px] text-stone-500 mb-1">Currency</div>
                <select
                  value={s.clip_price_currency || "cad"}
                  onChange={(e) => setS({ ...s, clip_price_currency: e.target.value })}
                  className="field-input"
                  data-testid="clip-currency-select"
                >
                  <option value="cad">CAD</option>
                  <option value="usd">USD</option>
                </select>
              </div>
            </div>
            <div>
              <div className="text-[10px] text-stone-500 mb-1">Preview watermark text</div>
              <input
                type="text"
                value={s.watermark_text ?? "Property of Elevated Arena"}
                onChange={(e) => setS({ ...s, watermark_text: e.target.value })}
                className="field-input"
                data-testid="watermark-text-input"
              />
            </div>
            <div>
              <div className="text-[10px] text-stone-500 mb-1">Preview length (seconds)</div>
              <input
                type="number"
                min="1"
                max="60"
                value={s.preview_seconds ?? 5}
                onChange={(e) => setS({ ...s, preview_seconds: Math.max(1, Math.min(60, parseInt(e.target.value, 10) || 5)) })}
                className="field-input !w-32"
                data-testid="preview-seconds-input"
              />
            </div>
            <div className="text-[10px] text-stone-500">
              Riders see a watermarked <code className="text-orange-400">{s.preview_seconds ?? 5}s</code> preview,
              then must pay <code className="text-orange-400">${(s.clip_price_default ?? 10).toFixed(2)} {(s.clip_price_currency || "cad").toUpperCase()}</code>
              &nbsp;to unlock the clean download.
            </div>
          </div>

          {/* Photographer drop passcode */}
          <div className="pt-3 border-t border-stone-800">
            <label className="block text-sm text-stone-300 mb-1">Photographer drop passcode</label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={s.drop_passcode || ""}
                onChange={(e) => setS({ ...s, drop_passcode: e.target.value })}
                placeholder="e.g. rodeo2026"
                className="input flex-1 font-mono"
                data-testid="drop-passcode-input"
              />
              {s.drop_passcode && (
                <a
                  href="/drop"
                  target="_blank"
                  rel="noreferrer"
                  className="text-[11px] text-orange-400 hover:underline whitespace-nowrap"
                  data-testid="drop-link"
                >
                  Open /drop ↗
                </a>
              )}
            </div>
            <div className="text-[10px] text-stone-500 mt-1">
              Share this passcode + the <code className="text-orange-400">/drop</code> URL with photographers.
              They upload from their phones, videos land in the Studio picker with a "New drop" banner.
              Leave empty to disable the drop page.
            </div>
          </div>

          {/* Vimeo watchlist poll intervals */}
          <div className="pt-3 border-t border-stone-800 space-y-3">
            <div className="text-sm text-stone-300 font-medium">Vimeo watchlist poll intervals</div>

            <div>
              <label className="block text-[11px] uppercase tracking-widest text-stone-400 mb-1">Event awaiting start (fast)</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  max={60}
                  value={s.vimeo_event_poll_minutes ?? 2}
                  onChange={(e) => setS({ ...s, vimeo_event_poll_minutes: Math.max(1, Math.min(60, parseInt(e.target.value, 10) || 2)) })}
                  className="input w-24 font-mono"
                  data-testid="vimeo-event-poll-minutes-input"
                />
                <span className="text-sm text-stone-400">minutes</span>
              </div>
              <div className="text-[10px] text-stone-500 mt-1">
                How often to check an event URL that hasn&apos;t started yet, so you catch it going live quickly.
                Default 2 · min 1 · max 60.
              </div>
            </div>

            <div>
              <label className="block text-[11px] uppercase tracking-widest text-stone-400 mb-1">Video transcode / regular check</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={5}
                  max={720}
                  value={s.vimeo_poll_minutes ?? 60}
                  onChange={(e) => setS({ ...s, vimeo_poll_minutes: Math.max(5, Math.min(720, parseInt(e.target.value, 10) || 60)) })}
                  className="input w-24 font-mono"
                  data-testid="vimeo-poll-minutes-input"
                />
                <span className="text-sm text-stone-400">minutes</span>
              </div>
              <div className="text-[10px] text-stone-500 mt-1">
                How often to check the Vimeo transcode status once an event is live / has a video ID.
                Default 60 · min 5 · max 720 (12 h). Click <b>Check now</b> on any row for an instant re-check.
              </div>
            </div>
          </div>

          <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="save-settings-btn">
            <Save size={14} /> {saving ? "Saving…" : "Save Settings"}
          </button>
        </div>
      </section>

      {/* AI Coaching Tips */}
      <section className="mb-10" data-testid="ai-tips-section">
        <div className="flex items-baseline gap-3 mb-3">
          <h2 className="font-display uppercase text-2xl font-bold flex items-center gap-2">
            <Sparkles size={18} className="text-orange-500" /> AI Coaching Tips
          </h2>
          <span className="font-mono-tc text-xs text-stone-500">{tips.length} active</span>
        </div>
        <p className="text-stone-400 text-sm mb-5 leading-relaxed">
          Add rules in plain English. Every tip is fed into the AI prompt as a strict instruction on the next AI Suggest run.
          Combined with your 👍/👎 feedback, this is how you teach the AI <em>your</em> standard for what makes a good clip.
        </p>

        {/* Add tip */}
        <div className="border border-stone-800 p-5 mb-5 bg-stone-900/30">
          <div className="label-tiny mb-2 flex items-center gap-2"><Plus size={12} /> New tip</div>
          <textarea
            value={newTip}
            onChange={(e) => setNewTip(e.target.value)}
            rows={2}
            placeholder="e.g. Team roping clips should never exceed 15 seconds. End the cut as soon as both ropes are taut."
            className="field-input mb-3"
            data-testid="new-tip-input"
          />
          <div className="flex flex-wrap gap-2 items-center">
            <select value={newCat} onChange={(e) => setNewCat(e.target.value)} className="field-input !w-auto" data-testid="new-tip-cat">
              {CATS.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
            </select>
            <button
              onClick={() => addTip(newTip, newCat)}
              disabled={adding || !newTip.trim()}
              className="btn-primary inline-flex items-center gap-1.5"
              data-testid="add-tip-btn"
            >
              <Plus size={14} /> Add Tip
            </button>
          </div>

          {/* Quick templates */}
          <div className="mt-5 pt-4 border-t border-stone-800">
            <div className="label-tiny mb-2 flex items-center gap-2"><Lightbulb size={11} /> Quick add</div>
            <div className="flex flex-wrap gap-1.5">
              {TEMPLATES.map((t, i) => (
                <button
                  key={i}
                  onClick={() => setNewTip(t)}
                  className="text-[11px] text-stone-300 px-2.5 py-1 border border-stone-800 hover:border-orange-700 hover:bg-stone-900 transition-colors"
                  data-testid={`tip-template-${i}`}
                >
                  {t.length > 50 ? t.slice(0, 50) + "…" : t}
                </button>
              ))}
            </div>
          </div>

          {/* Discipline-specific defaults */}
          <div className="mt-4 pt-4 border-t border-stone-800">
            <div className="label-tiny mb-2">Suggested for each discipline</div>
            <div className="space-y-1.5">
              {Object.entries(DEFAULT_TIPS).map(([cat, tip]) => (
                <button
                  key={cat}
                  onClick={() => addTip(tip, cat)}
                  className="block w-full text-left text-[11px] text-stone-400 px-3 py-2 border border-stone-800 hover:border-orange-700 hover:bg-stone-900 transition-colors"
                  data-testid={`tip-default-${cat}`}
                >
                  <span className="text-orange-500 uppercase tracking-widest font-bold mr-2">{CAT_NAME[cat]}</span>
                  <span className="text-stone-300">{tip}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Active tips list */}
        {tips.length === 0 ? (
          <div className="text-stone-500 text-sm italic px-2">No tips yet. The AI is using only its built-in rules + your 👍/👎 examples.</div>
        ) : (
          <div className="space-y-2">
            {tips.map((t) => (
              <TipRow key={t.id} tip={t} onChange={refreshTips} onDelete={() => delTip(t.id)} />
            ))}
          </div>
        )}
      </section>

      <DataMigrationPanel />
    </div>
  );
}

function DataMigrationPanel() {
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [report, setReport] = useState(null);

  const doExport = async () => {
    setExporting(true);
    try {
      const r = await api.get("/admin/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-");
      a.download = `rodeo-cut-studio-export-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${r.data.total_docs} records across ${Object.keys(r.data.collections).length} collections`);
    } catch (err) {
      toast.error("Export failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setExporting(false);
    }
  };

  const doImport = async (file, mode) => {
    if (!file) return;
    if (mode === "replace" && !window.confirm(
      "REPLACE mode will WIPE every collection in this database before loading the file. Continue?"
    )) return;
    setImporting(true);
    setReport(null);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (parsed.schema_version !== 1) throw new Error("Unsupported schema_version");
      const r = await api.post("/admin/import", {
        schema_version: parsed.schema_version,
        collections: parsed.collections,
        mode,
      });
      setReport(r.data);
      toast.success(`Import ${mode} complete`);
    } catch (err) {
      toast.error("Import failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setImporting(false);
    }
  };

  return (
    <section className="border border-stone-800 bg-stone-950/40 p-6 mt-10" data-testid="data-migration-panel">
      <div className="flex items-center gap-3 mb-2">
        <Download size={20} className="text-orange-500" />
        <h2 className="font-display uppercase text-2xl font-bold">Data Migration</h2>
      </div>
      <p className="text-stone-400 text-sm mb-5">
        Export your training data, coaching tips, reel templates, and rider dashboards to a JSON file —
        then import that file into a new environment (production deploy, staging, etc.). S3 keys keep working
        as long as both environments share the same bucket.
        <span className="text-amber-400/90"> The AI is not fine-tuned; it learns purely from the cuts and tips in this database, so migrating these collections preserves all "learning."</span>
      </p>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="border border-stone-800 p-4">
          <div className="font-bold uppercase tracking-widest text-xs text-orange-400 mb-1">Step 1 · Export from preview</div>
          <p className="text-stone-400 text-xs mb-3">Downloads a single JSON file with every cut, clip, reel template, coaching tip, rider profile, comment, and setting.</p>
          <button
            onClick={doExport}
            disabled={exporting}
            className="btn-primary inline-flex items-center gap-2"
            data-testid="export-data-btn"
          >
            <Download size={14} /> {exporting ? "Exporting…" : "Export All Data"}
          </button>
        </div>

        <div className="border border-stone-800 p-4">
          <div className="font-bold uppercase tracking-widest text-xs text-orange-400 mb-1">Step 2 · Import into production</div>
          <p className="text-stone-400 text-xs mb-3">
            <strong>Upsert</strong> = safe (keeps existing, adds/updates by ID).
            <strong className="text-rose-400"> Replace</strong> = wipes collections first.
          </p>
          <div className="flex gap-2 flex-wrap">
            <label className="btn-secondary inline-flex items-center gap-2 cursor-pointer" data-testid="import-upsert-label">
              <Upload size={14} /> {importing ? "Importing…" : "Import (Upsert)"}
              <input
                type="file"
                accept="application/json,.json"
                disabled={importing}
                className="hidden"
                onChange={(e) => doImport(e.target.files?.[0], "upsert")}
                data-testid="import-upsert-input"
              />
            </label>
            <label className="btn-secondary inline-flex items-center gap-2 cursor-pointer hover:!border-rose-700 hover:!text-rose-400" data-testid="import-replace-label">
              <Upload size={14} /> Import (Replace)
              <input
                type="file"
                accept="application/json,.json"
                disabled={importing}
                className="hidden"
                onChange={(e) => doImport(e.target.files?.[0], "replace")}
                data-testid="import-replace-input"
              />
            </label>
          </div>
        </div>
      </div>

      {report && (
        <div className="mt-5 border border-stone-800 p-4 text-xs font-mono-tc" data-testid="import-report">
          <div className="text-emerald-400 mb-2 uppercase tracking-widest">Import report · mode = {report.mode}</div>
          {Object.entries(report.collections || {}).map(([coll, stats]) => (
            <div key={coll} className="text-stone-300">
              <span className="text-orange-400">{coll}</span>: {stats.inserted} new, {stats.updated} updated, {stats.skipped} skipped (out of {stats.total_in})
            </div>
          ))}
          {report.skipped_collections?.length > 0 && (
            <div className="text-stone-500 mt-2">Skipped unknown collections: {report.skipped_collections.join(", ")}</div>
          )}
        </div>
      )}
    </section>
  );
}

function TipRow({ tip, onChange, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(tip.text);
  const [category, setCategory] = useState(tip.category || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!text.trim()) {
      toast.error("Tip cannot be empty");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/coaching-tips/${tip.id}`, { text, category });
      toast.success("Tip updated");
      setEditing(false);
      onChange();
    } catch (err) {
      toast.error("Failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setText(tip.text);
    setCategory(tip.category || "");
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="border border-orange-700/60 px-4 py-3 bg-stone-900/50" data-testid={`tip-${tip.id}`}>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={2}
          className="field-input mb-2"
          data-testid={`tip-edit-text-${tip.id}`}
          autoFocus
        />
        <div className="flex flex-wrap gap-2 items-center">
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="field-input !w-auto" data-testid={`tip-edit-cat-${tip.id}`}>
            {CATS.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
          </select>
          <button onClick={save} disabled={saving} className="btn-primary !py-2 !px-3 inline-flex items-center gap-1.5 text-xs" data-testid={`tip-save-${tip.id}`}>
            <Save size={12} /> {saving ? "Saving…" : "Save"}
          </button>
          <button onClick={cancel} className="btn-secondary !py-2 !px-3 text-xs" data-testid={`tip-cancel-${tip.id}`}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-stone-800 px-4 py-3 flex items-start gap-3 hover:bg-stone-900/40 group" data-testid={`tip-${tip.id}`}>
      <span className={`shrink-0 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 mt-0.5 ${tip.category ? "text-orange-500 border border-orange-700/40" : "text-stone-400 border border-stone-700"}`}>
        {tip.category ? CAT_NAME[tip.category] || tip.category : "All"}
      </span>
      <div className="flex-1 text-sm text-stone-200">{tip.text}</div>
      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => setEditing(true)}
          className="text-stone-500 hover:text-orange-400"
          title="Edit"
          data-testid={`tip-edit-${tip.id}`}
        >
          <Pencil size={14} />
        </button>
        <button
          onClick={onDelete}
          className="text-stone-500 hover:text-rose-400"
          title="Delete"
          data-testid={`tip-delete-${tip.id}`}
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}
